import React, { useCallback, useState } from "react";

import AddCircleOutline from "@mui/icons-material/AddCircleOutline";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import TableChartOutlinedIcon from "@mui/icons-material/TableChartOutlined";
import { Box, Stack, Typography } from "@mui/material";
import GettingStartedBannerImg from "assets/images/GettingStartedBanner.png";
import { DealCreate } from "components/modals";
import { useModals, useNavigate, usePrompt } from "hooks";
import { useAccountSelector } from "store";

import { InviteMembersModal } from "../Invite-members/InviteMembersModal";

interface ActionItem {
  id: string;
  icon: React.ReactNode;
  title: string;
  subtitle: string;
  onClick: () => void;
}

export const GettingStarted: React.FC = () => {
  const navigate = useNavigate();
  const openPrompt = usePrompt();
  const { freemium_account_settings } = useAccountSelector() || {};
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const { openDataroomInfoConnect } = useModals();

  const handleAddDeal = useCallback(async () => {
    const data = await openPrompt({ Component: DealCreate, props: {}, disableBackdropClose: true });
    if (!data) return;
    navigate({ route: "deal", params: { deal_id: data.id } });
  }, [openPrompt, navigate]);

  const actions: ActionItem[] = [
    {
      id: "connect-data-portals",
      icon: <CloudUploadIcon sx={{ fontSize: 18, color: "#C09560" }} />,
      title: "Connect data portals",
      subtitle: `${(freemium_account_settings?.max_datarooms_connections || 0) - (freemium_account_settings?.number_of_datarooms_connections || 0)} datarooms are available`,
      onClick: openDataroomInfoConnect
    },
    {
      id: "run-portfolio-analysis",
      icon: <TableChartOutlinedIcon sx={{ fontSize: 18, color: "#C09560" }} />,
      title: "Run Portfolio Analysis",
      subtitle: "IRR, TVPI, Benchmarking...",
      onClick: () => navigate({ route: "reports" })
    },
    {
      id: "add-deal",
      icon: <AddCircleOutline sx={{ fontSize: 18, color: "#C09560" }} />,
      title: "Add Deal",
      subtitle: `Up to ${freemium_account_settings?.max_deals || 10} deals in Free Plan`,
      onClick: handleAddDeal
    }
    // {
    //   id: "invite-team-members",
    //   icon: <PeopleAltOutlinedIcon sx={{ fontSize: 18, color: "#C09560" }} />,
    //   title: "Invite team members",
    //   subtitle: "Up to 3 users in Free Plan",
    //   onClick: () => setInviteModalOpen(true)
    // }
  ];

  return (
    <>
      <InviteMembersModal open={inviteModalOpen} onClose={() => setInviteModalOpen(false)} />
      <Stack
        direction="row"
        sx={{
          backgroundColor: "#071F35",
          borderRadius: "8px",
          overflow: "hidden",
          position: "relative",
          width: "100%",
          height: "216px",
          "@media (max-width: 1100px)": {
            height: "auto",
            minHeight: "216px"
          }
        }}
      >
        <Box
          component="img"
          src={GettingStartedBannerImg}
          alt=""
          aria-hidden="true"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%)",
            height: "100%",
            width: "auto",
            pointerEvents: "none",
            userSelect: "none",
            zIndex: 0
          }}
        />

        <Stack
          sx={{
            flex: "0 0 auto",
            padding: "24px",
            justifyContent: "start",
            zIndex: 1,
            minWidth: "260px",
            position: "relative"
          }}
        >
          <Typography
            sx={{
              color: "#4FC3A1",
              fontSize: "14px",
              lineHeight: "16px",
              fontWeight: 400,
              letterSpacing: "0.08em",
              textTransform: "uppercase",
              mb: 2
            }}
            variant="body3"
          >
            Getting Started
          </Typography>
          <Typography
            sx={{
              color: "#FFFFFF",
              fontSize: "24px",
              lineHeight: "32px",
              fontWeight: 700,
              mb: 2
            }}
            variant="h5"
          >
            Your workspace is set up.
          </Typography>
          <Typography
            sx={{
              color: "#92A7BC",
              fontSize: "16px",
              lineHeight: "24px",
              maxWidth: "300px"
            }}
          >
            You&apos;ve created your first deals. Now connect your portfolio data, run analytics, and invite your team.
          </Typography>
        </Stack>

        {/* Spacer — lets the image show through in the middle */}
        <Box sx={{ flex: "1 1 auto" }} />

        <Stack
          sx={{
            flex: "0 0 auto",
            justifyContent: "center",
            zIndex: 1,
            rowGap: "10px",
            minWidth: "280px",
            position: "relative",
            padding: "26px 24px"
          }}
        >
          {actions.map((action) => (
            <Stack
              key={action.id}
              direction="row"
              alignItems="center"
              gap="12px"
              onClick={action.onClick}
              sx={{
                padding: "6px",
                flex: 1,
                cursor: "pointer",
                backgroundColor: "#496682",
                transition: "background-color 0.15s ease",
                borderRadius: "8px"
              }}
            >
              <Box
                sx={{
                  width: 40,
                  height: 40,
                  borderRadius: "8px",
                  backgroundColor: "#071F35",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0
                }}
              >
                {action.icon}
              </Box>

              <Stack flex={1} gap="2px">
                <Typography
                  sx={{
                    color: "#FFFFFF",
                    fontWeight: 500
                  }}
                  variant="body2"
                >
                  {action.title}
                </Typography>
                <Typography
                  sx={{
                    color: "#DBE8F5"
                  }}
                  variant="body3"
                >
                  {action.subtitle}
                </Typography>
              </Stack>

              <ChevronRightIcon sx={{ color: "#FFFFFF", fontSize: 20, flexShrink: 0 }} />
            </Stack>
          ))}
        </Stack>
      </Stack>
    </>
  );
};
